import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient, { get, post, patch } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import FiltroFechas from '../finanzas-cxp/FiltroFechas.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Retiros -- réplica de `ListadoRetiros.jsx` + `RetirosCreate.jsx` (V1): filtros Fecha
 * Inicio/Fin, buscador, PDF/Excel, tabla Numero/Fecha/Folio/Socio/Observaciones/
 * Importe/Acciones (editar + cancelar; los cancelados se pintan en rojo con
 * "CANCELADO") y fila TOTAL. Alta: Fecha, Importe, Socio, Banco (solo MXN),
 * Observaciones (default "NA") y Folio. NO usa `ResourcePage` (la baja es una acción
 * de negocio propia: `PATCH /retiros/:id/cancelar`, ver DUDAS.md D-11.4).
 */
function valoresIniciales(row) {
  if (!row) return { fecha: today(), total: 0, socios: '', bancos: '', observaciones: 'NA', folio: '' };
  return {
    fecha: (row.fecha || '').slice(0, 10), total: row.importe, socios: row._idSocio || '', bancos: row._idBanco || '',
    observaciones: row.observaciones || '', folio: row.folio || '',
  };
}

function RetiroForm({ open, onClose, title, initial, socios, bancos, onSubmit, esEdicion }) {
  const [v, setV] = useState(valoresIniciales(initial));
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) setV(valoresIniciales(initial)); }, [open, initial]);

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.bancos) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar un Banco' }); return; }
    if (esEdicion) {
      if (!v.socios) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar un Socio' }); return; }
      if (!v.total || Number(v.total) <= 0) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'El importe debe ser mayor a 0' }); return; }
      if (!v.fecha) { Swal.fire({ icon: 'error', title: 'Oops...', text: 'Debe seleccionar una fecha' }); return; }
    }
    const r = await Swal.fire(esEdicion
      ? {
        title: '¿Estás seguro?', text: 'Se actualizará el retiro y se modificarán los saldos correspondientes', icon: 'warning',
        showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Sí, Actualizar!', cancelButtonText: 'Cancelar',
      }
      : {
        title: 'Estas seguro?', text: 'Se registrará el retiro', icon: 'warning',
        showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Retirar!',
      });
    if (!r.isConfirmed) return;
    setGuardando(true);
    try {
      await onSubmit(v);
      Swal.fire(esEdicion ? '¡Perfecto!' : 'Good job!', esEdicion ? 'Retiro actualizado con éxito' : 'Creado con exito', 'success');
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: esEdicion ? 'Algo salió mal al actualizar el retiro' : 'Something went wrong!', footer: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">{esEdicion ? 'Cancelar' : 'Regresar'}</button>
          <button type="submit" form="retiro-form" disabled={guardando} className="btn-primary">{esEdicion ? 'Actualizar' : 'Registrar'}</button>
        </>
      )}
    >
      <form id="retiro-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Importe</label>
          <input type="number" min="0" step="any" value={v.total} onChange={(e) => set('total', e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Socio</label>
          <select value={v.socios} onChange={(e) => set('socios', e.target.value)} className="select-base" required={!esEdicion}>
            <option value="">Selecciona</option>
            {[...socios].sort((a, b) => (a.name > b.name ? 1 : -1)).map((s) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Banco</label>
          <select value={v.bancos} onChange={(e) => set('bancos', e.target.value)} className="select-base">
            <option value="">Selecciona un Banco</option>
            {[...bancos].filter((b) => b.moneda === 'MXN').sort((a, b) => (a.banco > b.banco ? 1 : -1)).map((b) => (
              <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Observaciones</label>
          <input type="text" required value={v.observaciones} onChange={(e) => set('observaciones', e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Folio</label>
          <input type="text" required value={v.folio} onChange={(e) => set('folio', e.target.value)} className="input-base" />
        </div>
      </form>
    </Modal>
  );
}

export default function RetirosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('retiros:ver');
  const puedeCrear = hasPermiso('retiros:crear');
  const puedeEditar = hasPermiso('retiros:editar');
  const puedeCancelar = hasPermiso('retiros:eliminar');

  const [filas, setFilas] = useState([]);
  const [socios, setSocios] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(daysAgo(30));
  const [fechaFin, setFechaFin] = useState(today());

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [data, sociosData, bancosData] = await Promise.all([
        get(`/retiros/fechas/${fechaInicio}/${fechaFin}`),
        get('/socios'),
        apiClient.get('/bancos', { params: { limit: 500 } }).then((r) => r.data.data),
      ]);
      setSocios(sociosData || []);
      setBancos(bancosData || []);
      setFilas((data || []).map((r) => {
        const socio = primero(r.socios);
        const banco = primero(r.bancos);
        return {
          _id: r._id,
          numero: r.idRetiro,
          fecha: r.fecha,
          folio: r.folio || '',
          socio: socio?.name || '',
          _idSocio: socio?._id,
          _idBanco: banco?._id,
          observaciones: r.observaciones || '',
          importe: r.total,
          is_active: r.is_active || 'Si',
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { if (puedeVer) cargar(); }, [cargar, puedeVer]);

  async function guardar(v) {
    const payload = {
      fecha: v.fecha, total: Number(v.total), observaciones: v.observaciones, bancos: v.bancos, socios: v.socios, folio: v.folio,
    };
    if (editando) await patch(`/retiros/${editando._id}`, payload);
    else await post('/retiros', payload);
    await cargar();
  }

  async function cancelar(row) {
    const r = await Swal.fire({
      title: '¿Estás seguro?', text: 'Se cancelará el retiro y se revertirán todos los saldos. Esta acción no se puede deshacer.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6', confirmButtonText: 'Sí, Cancelar Retiro!', cancelButtonText: 'No, mantener',
    });
    if (!r.isConfirmed) return;
    try {
      await patch(`/retiros/${row._id}/cancelar`);
      Swal.fire('¡Cancelado!', 'El retiro ha sido cancelado exitosamente', 'success');
      await cargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cancelar el retiro', footer: e.message });
    }
  }

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(filas, {
    searchFields: ['socio', 'observaciones', 'importe', 'numero'], defaultSortBy: 'numero', defaultSortOrder: 'desc', perPage: 50,
  });

  // Columnas EXACTAS del PDF/Excel de V1 (`ListadoRetiros.jsx`): Numero, Fecha, Observaciones, Importe.
  const columnasPdf = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const columnasExcel = columnasPdf.map((c) => (c.key === 'importe' ? { key: 'importe', label: 'Importe' } : c));
  const filasExport = filtradas.filter((r) => r.is_active !== 'No');
  const totalExport = filasExport.reduce((a, r) => a + (r.importe || 0), 0);
  const opcionesPdf = () => ({ fileName: 'Retiros', subtitulo: `${fechaInicio} - ${fechaFin}`, totalCols: { observaciones: 'Total', importe: `$${new Intl.NumberFormat('en-US').format(totalExport)}` } });

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'folio', label: 'Folio', sortable: true },
    { key: 'socio', label: 'Socio', sortable: true },
    { key: 'observaciones', label: 'Observaciones', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, render: (r) => formatMoney(r.importe) },
    {
      key: '__acciones', label: 'Acciones', sortable: false,
      render: (row) => (row.is_active === 'No' ? <span className="badge-danger">CANCELADO</span> : (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            title="Editar Retiro" aria-label="Editar Retiro" disabled={!puedeEditar}
            className="p-1.5 rounded-md text-warning-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => { setEditando(row); setFormOpen(true); }}
          >
            <Pencil size={15} />
          </button>
          <button
            title="Cancelar Retiro" aria-label="Cancelar Retiro" disabled={!puedeCancelar}
            className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => cancelar(row)}
          >
            <X size={15} />
          </button>
        </div>
      )),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Reporte de Retiros</h1>
        {puedeCrear && (
          <button onClick={() => { setEditando(null); setFormOpen(true); }} className="btn-primary">Nuevo Retiro</button>
        )}
      </div>

      <FiltroFechas inicio={fechaInicio} fin={fechaFin} onInicio={setFechaInicio} onFin={setFechaFin} />

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTablePdf('Listado Abonos', columnasPdf, filasExport, opcionesPdf())}>PDF</button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => setMailOpen(true)}>eMail</button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTableExcel('ListadoRetiros', columnasExcel, filasExport, { fileName: 'ListadoRetiros', sheetName: 'ListadoRetiros' })}>Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ observaciones: 'TOTAL', importe: formatMoney(pageRows.reduce((a, r) => a + (r.importe || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado de Retiros"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Listado Abonos', columnasPdf, filasExport, opcionesPdf());
          await enviarCorreoAdjunto({
            subject: 'Listado Retiros', email, fileName: 'ListadoRetiros.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Retiros.'),
          });
        }}
      />
      <RetiroForm
        open={formOpen} onClose={() => setFormOpen(false)} title={editando ? 'Editar Retiro' : 'Captura un Retiro'}
        initial={editando} socios={socios} bancos={bancos} onSubmit={guardar} esEdicion={!!editando}
      />
    </div>
  );
}
